import { Search } from "@deere/fuel-react";
import { Dispatch, Ref, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { D2DOrdersActions, setFilterAction } from "../../reducers/actions";
import { D2DOrdersFilters } from "../../types";

export interface D2DOrdersSearchFilterProps {
  inputRef: Ref<any>;
  field: keyof D2DOrdersFilters;
  filters: D2DOrdersFilters;
  dispatch: Dispatch<D2DOrdersActions>;
}

const D2DOrdersSearchFilter = ({
  inputRef,
  field,
  filters,
  dispatch,
}: D2DOrdersSearchFilterProps) => {
  const [filterTerm, setFilterTerm] = useState(filters[field] ?? "");
  const [startFilter, setStartFilter] = useState(false);
  const filterTermRef = useRef(filterTerm);
  const { t } = useTranslation(["d2d", "common"]);

  useEffect(() => {
    filterTermRef.current = filterTerm;
  }, [filterTerm]);

  useEffect(() => {
    const externalValue = filters[field] ?? "";
    if (externalValue !== filterTerm) {
      setFilterTerm(externalValue);
    }
  }, [filters[field], field]);

  useEffect(() => {
    if (!startFilter) return undefined;

    const timeoutId = setTimeout(() => {
      const updatedFilters =
        filterTermRef.current === ""
          ? { ...filters, [field]: undefined }
          : { ...filters, [field]: filterTermRef.current };

      dispatch(setFilterAction(updatedFilters));
      setStartFilter(false);
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [startFilter]);

  return (
    <Search
      data-testid={`d2d-orders-filter-${field}`}
      placeholder={t("common:table.headerFilter.search.placeholder") || ""}
      ref={inputRef}
      sx={{ ".MuiOutlinedInput-input": { textOverflow: "ellipsis" } }}
      value={filterTerm}
      onChange={(event) => {
        setFilterTerm(event.target.value);
        setStartFilter(true);
      }}
      onClear={() => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [field]: _, ...updatedFilters } = filters;
        dispatch(setFilterAction(updatedFilters));
        setFilterTerm("");
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
        }
      }}
    />
  );
};

export default D2DOrdersSearchFilter;
